import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { logout } from '../services/auth';
import {
  Button,
  Typography,
  Box,
  AppBar,
  Toolbar,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardStyles from '../styles/DashboardStyles';
import heroImage1 from '../assets/heroimage1.png';
import logo from '../assets/logo-white.png';

const { NavigationBar: NavStyles, Dashboard: DashStyles } = DashboardStyles;

// NavigationBar Component
const NavigationBar = ({ currentView, setCurrentView, handleLogout, navigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <AppBar position="static" sx={NavStyles.appBar(currentView, null, heroImage1)}>
      <Toolbar sx={NavStyles.toolbar}>
        <img src={logo} alt="Logo" style={NavStyles.logo} />
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <IconButton
            color="inherit"
            onClick={toggleMobileMenu}
            sx={{ p: 1 }}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
        <Box sx={NavStyles.navButtonsContainer}>
          <Button color="inherit" onClick={() => { setCurrentView('labelClaims'); setMobileMenuOpen(false); }} sx={NavStyles.navButton}>
            Label Claims
          </Button>
          <Button color="inherit" onClick={() => { setCurrentView('orderDetails'); setMobileMenuOpen(false); }} sx={NavStyles.navButton}>
            Order Details
          </Button>
          <Button color="inherit" onClick={() => { navigate('/user-settings'); setMobileMenuOpen(false); }} sx={NavStyles.navButton}>
            User Settings
          </Button>
        </Box>
        {mobileMenuOpen && (
          <Box sx={NavStyles.mobileMenu}>
            <Button color="inherit" onClick={() => { setCurrentView('labelClaims'); setMobileMenuOpen(false); }} sx={NavStyles.navButton}>
              Label Claims
            </Button>
            <Button color="inherit" onClick={() => { setCurrentView('orderDetails'); setMobileMenuOpen(false); }} sx={NavStyles.navButton}>
              Order Details
            </Button>
            <Button color="inherit" onClick={() => { navigate('/user-settings'); setMobileMenuOpen(false); }} sx={NavStyles.navButton}>
              User Settings
            </Button>
            <Button color="inherit" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} sx={NavStyles.logoutButton}>
              Logout
            </Button>
          </Box>
        )}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Button color="inherit" onClick={handleLogout} sx={NavStyles.logoutButton}>
            Logout
          </Button>
        </Box>
      </Toolbar>
      <Box sx={NavStyles.heroContent(currentView)}>
        <Typography variant="h3" sx={NavStyles.viewTitle}>
          {currentView === 'labelClaims' ? 'Explore Label Claims' : 'Order History'}
        </Typography>
      </Box>
    </AppBar>
  );
};

// Function to load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      console.error("Failed to load Razorpay script");
      reject(new Error("Failed to load Razorpay SDK"));
    };
    const timeout = setTimeout(() => {
      reject(new Error("Razorpay script loading timed out. Please check your network and try again."));
    }, 10000);
    script.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    script.onerror = () => {
      clearTimeout(timeout);
      reject(new Error("Failed to load Razorpay SDK"));
    };
    document.body.appendChild(script);
  });
};

// Fetching dynamic data for grades, countries, and diseases
const fetchGrades = async (token) => {
  try {
    const response = await api.get('/label_claims/grades', {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Fetched grades:', response.data);
    return response.data;
  } catch (err) {
    console.error('Failed to fetch grades:', {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message,
    });
    return [];
  }
};

const fetchDiseases = async (token) => {
  try {
    const response = await api.get('/label_claims/diseases', {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Fetched diseases:', response.data);
    return response.data;
  } catch (err) {
    console.error('Failed to fetch diseases:', {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message,
    });
    return [];
  }
};

function Dashboard() {
  const [labelClaims, setLabelClaims] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [filters, setFilters] = useState({
    disease: '',
    country: 'India',
    grade: '',
  });
  const [user, setUser] = useState(null);
  const [grades, setGrades] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [currentView, setCurrentView] = useState('labelClaims');
  const [showClaimDetails, setShowClaimDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch user details and order details only once on mount
  useEffect(() => {
    fetchUserDetails();
    fetchOrderDetails();
  }, []);

  // Fetch grades and diseases once user is authenticated
  useEffect(() => {
    if (user) {
      loadGradesAndDiseases();
    }
  }, [user]);

  // Fetch label claims when filters or view changes
  useEffect(() => {
    if (currentView === 'labelClaims') {
      fetchLabelClaims();
    }
  }, [filters, currentView]);

  const getErrorMessage = (error) => {
    if (!error) return "An unknown error occurred.";
    if (error.response?.status === 401) {
      logout();
      navigate("/login");
      return "Session expired. Please log in again.";
    }
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        return detail.map((err) => `${err.loc?.join('.')}: ${err.msg}`).join("; ");
      }
      return detail;
    }
    return error.message || "An unexpected error occurred.";
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await api.get('/auth/user/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Fetched user details:', response.data);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await api.get('/orders/order-history', {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Fetched order details:', response.data);
      const normalizedOrders = (response.data.orders || []).map(order => ({
        ...order,
        id: order.id || order.order_id,
      }));
      setOrderDetails(normalizedOrders);
    } catch (error) {
      console.error('Failed to fetch order details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const loadGradesAndDiseases = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [fetchedGrades, fetchedDiseases] = await Promise.all([
        fetchGrades(token),
        fetchDiseases(token),
      ]);
      setGrades(fetchedGrades);
      setDiseases(fetchedDiseases);
    } catch (error) {
      console.error('Failed to load grades and diseases:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchLabelClaims = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filters.grade) params.grade = filters.grade;
      if (filters.disease) params.disease = filters.disease;
      const response = await api.get('/label_claims/', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Fetched labelClaims:', response.data);
      setLabelClaims(response.data || []);
    } catch (err) {
      console.error('Failed to fetch label claims:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const handleClaimSelection = (claimId) => {
    setSelectedClaim(claimId);
  };

  const handleOrderNow = () => {
    if (!selectedClaim) {
      alert('Please select a claim.');
      return;
    }
    const selectedClaims = labelClaims.filter((claim) => claim.id === selectedClaim);
    if (!selectedClaims.length) {
      alert('Selected claim is invalid.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You are not authenticated. Please log in again.');
      logout();
      navigate('/login');
      return;
    }
    console.log('Navigating with selectedClaimDetails:', selectedClaims);
    navigate('/payment', { state: { selectedClaimDetails: selectedClaims } });
  };

  const handlePayRemaining = async (orderId, remainingAmount, labelClaimId) => {
    setPaymentLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      console.log("Initiating remaining payment for order ID:", orderId);
      const response = await api.post(
        `/orders/${orderId}/pay-remaining`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { razorpay_order: razorpayOrder } = response.data;
      if (!razorpayOrder?.id) throw new Error('Invalid Razorpay order response');

      const isRazorpayLoaded = await loadRazorpayScript();
      if (!isRazorpayLoaded) {
        throw new Error("Failed to load Razorpay SDK.");
      }

      if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
        throw new Error("Razorpay key is not configured.");
      }

      const razorpayOptions = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency || 'INR',
        name: 'DealSphere',
        description: `Remaining payment for Order ID ${orderId}`,
        order_id: razorpayOrder.id,
        handler: async function (response) {
          try {
            const paymentSuccessResponse = await api.post(
              '/orders/payment-success',
              {
                payment_id: response.razorpay_payment_id,
                order_id: razorpayOrder.id,
                label_claim_id: labelClaimId,
                amount: remainingAmount,
                payment_type: 'full_payment',
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('Payment success:', paymentSuccessResponse.data);
            alert('Payment successful! Dossier is now available.');
            fetchOrderDetails();
          } catch (error) {
            console.error('Payment verification failed:', {
              status: error.response?.status,
              data: error.response?.data,
              message: error.message,
            });
            alert(`Payment verification failed: ${getErrorMessage(error)}`);
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.contact || '',
        },
        theme: { color: '#66202D' },
      };

      const razorpay = new window.Razorpay(razorpayOptions);
      razorpay.on('payment.failed', (response) => {
        console.error('Razorpay payment failed:', response.error);
        alert(`Payment failed: ${response.error.description || 'Please try again.'}`);
      });
      razorpay.on('payment.cancel', async () => {
        console.log('Payment canceled by user for order:', orderId);
        try {
          await api.post(
            `/orders/${orderId}/cancel`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('Order marked as cancelled in backend');
          alert('Payment was canceled. The order has been marked as cancelled.');
          fetchOrderDetails();
        } catch (error) {
          console.error('Failed to cancel order:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          });
          alert(`Failed to update order status: ${getErrorMessage(error)}`);
        }
      });
      razorpay.open();
    } catch (error) {
      console.error('Failed to initiate remaining payment:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDownloadFile = async (labelClaimId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await api.get(`/orders/download/${labelClaimId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${labelClaimId}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Failed to download file:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleShowClaimDetails = (claim) => {
    setSelectedClaim(claim.id);
    setShowClaimDetails(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={DashStyles.container}>
      <NavigationBar currentView={currentView} setCurrentView={setCurrentView} handleLogout={handleLogout} navigate={navigate} />
      <Grid container sx={{ flex: 1, padding: { xs: '0 16px', md: '0 117px' }, justifyContent: 'center', marginTop: { xs: '-40px', md: '-70px' }, zIndex: 2 }}>
        <Grid item xs={12}>
          <Box sx={DashStyles.contentBox}>
            {currentView === 'labelClaims' && (
              <>
                <Grid container spacing={2} sx={{ marginBottom: '20px', marginLeft: '0px' }}>
                  {['grade', 'country', 'disease'].map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <TextField
                        select
                        name={field}
                        value={filters[field]}
                        onChange={handleFilterChange}
                        variant="outlined"
                        size="small"
                        label={
                          field === 'grade'
                            ? 'Grade'
                            : field === 'country'
                            ? 'Country'
                            : 'Disease'
                        }
                        disabled={field === 'country'}
                        sx={{
                          ...DashStyles.filterTextField,
                          width: { xs: '100%', md: '240px' },
                          backgroundColor: 'rgb(250, 250, 250)',
                        }}
                      >
                        {field === 'country' ? (
                          <MenuItem value="India">India</MenuItem>
                        ) : field === 'grade' ? (
                          [
                            <MenuItem value="" key="all">
                              All Grades
                            </MenuItem>,
                            ...grades.map((grade) => (
                              <MenuItem key={grade} value={grade}>
                                {grade}
                              </MenuItem>
                            )),
                          ]
                        ) : (
                          [
                            <MenuItem value="" key="all">
                              All Diseases
                            </MenuItem>,
                            ...diseases.map((disease) => (
                              <MenuItem key={disease} value={disease}>
                                {disease}
                              </MenuItem>
                            )),
                          ]
                        )}
                      </TextField>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ width: '100%', margin: '0 auto', borderRadius: '12px', overflowX: { xs: 'auto', sm: 'hidden' } }}>
                  <Box sx={{ display: 'flex', padding: '12px 0', borderBottom: 'none', minWidth: { xs: '600px', sm: 'auto' } }}>
                    {['Select', 'ID', 'Grade', 'Label Claim', 'Condition', 'Amount', 'Actions'].map((header, index) => (
                      <Box
                        key={header}
                        sx={{
                          flex: `0 0 ${[5, 5, 5, 15, 20, 10, 10][index]}%`,
                          padding: '0 8px',
                          textAlign: index === 6 ? 'right' : 'left',
                          minWidth: index === 6 ? { xs: '100px', md: '120px' } : 'auto',
                        }}
                      >
                        <Typography sx={DashStyles.tableHeader}>{header}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ padding: '10px 0' }}>
                    {labelClaims.length > 0 ? (
                      labelClaims.map((claim, index) => (
                        <Box
                          key={claim.id}
                          sx={{ ...DashStyles.tableRow }}
                        >
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 5%' }, padding: '0 8px', display: 'flex', alignItems: 'center', minHeight: '100%' }}>
                            <FormControl>
                              <RadioGroup
                                name="claimSelection"
                                value={selectedClaim}
                                onChange={() => handleClaimSelection(claim.id)}
                              >
                                <FormControlLabel
                                  value={claim.id}
                                  control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '16px' } }} />}
                                  label=""
                                  sx={{ margin: 0 }}
                                />
                              </RadioGroup>
                            </FormControl>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 5%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ ...DashStyles.tableCell, whiteSpace: 'nowrap' }}>{claim.id}</Typography>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 5%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ ...DashStyles.tableCell, whiteSpace: 'nowrap' }}>{claim.grade}</Typography>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 15%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={DashStyles.tableCell}>{claim.label_claim}</Typography>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 20%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={DashStyles.tableCell}>{claim.condition}</Typography>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 10%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ ...DashStyles.tableCellBold, whiteSpace: 'nowrap' }}>
                              ₹{claim.amount ?? 'N/A'}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              flex: { xs: '100%', sm: '0 0 10%' },
                              padding: '0 8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                              minWidth: { xs: 'auto', md: '120px' },
                            }}
                          >
                            <Button
                              onClick={() => handleShowClaimDetails(claim)}
                              variant="text"
                              sx={DashStyles.actionButton}
                            >
                              View Details
                            </Button>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ padding: '20px', textAlign: 'center' }}>
                        <Typography>No label claims available.</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'center', marginTop: '30px' }}>
                  <Button
                    variant="contained"
                    onClick={handleOrderNow}
                    sx={DashStyles.orderButton}
                    disabled={!selectedClaim}
                  >
                    Order Now
                  </Button>
                </Box>
              </>
            )}
            {currentView === 'orderDetails' && (
              <>
                <Box sx={{ width: '100%', margin: '0 auto', borderRadius: '12px', overflowX: { xs: 'auto', sm: 'hidden' } }}>
                  <Box sx={{ display: 'flex', padding: '12px 0', borderBottom: 'none', minWidth: { xs: '600px', sm: 'auto' } }}>
                    {['Order ID', 'Label Claim', 'Total Amount', 'Status', 'Actions'].map((header, index) => (
                      <Box
                        key={header}
                        sx={{
                          flex: `0 0 ${[10, 20, 15, 15, 20][index]}%`,
                          padding: '0 8px',
                          textAlign: index === 4 ? 'right' : 'left',
                          minWidth: index === 4 ? { xs: '120px', md: '150px' } : 'auto',
                        }}
                      >
                        <Typography sx={DashStyles.tableHeader}>{header}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ padding: '10px 0' }}>
                    {orderDetails.length > 0 ? (
                      orderDetails.map((order, index) => (
                        <Box
                          key={order.id || `order-${index}`}
                          sx={{ ...DashStyles.tableRow }}
                        >
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 10%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ ...DashStyles.tableCell, whiteSpace: 'nowrap' }}>
                              {order.id || 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 20%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={DashStyles.tableCell}>
                              {order.label_claim || 'Unknown Claim'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 15%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ ...DashStyles.tableCellBold, whiteSpace: 'nowrap' }}>
                              ₹{order.amount != null ? order.amount : 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: { xs: '100%', sm: '0 0 15%' }, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                            <Typography sx={DashStyles.statusText(order.status || 'pending')}>
                              {order.status === 'booking_paid' ? 'Booking Paid' :
                               order.status === 'fully_paid' ? 'Fully Paid' :
                               order.status === 'cancelled' ? 'Cancelled' :
                               order.status === 'failed' ? 'Failed' :
                               order.status === 'pending' ? 'Pending' : 'Unknown'}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              flex: { xs: '100%', sm: '0 0 20%' },
                              padding: '0 8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                              minWidth: { xs: 'auto', md: '150px' },
                              gap: '10px',
                            }}
                          >
                            {order.status === 'fully_paid' && order.payment_status === 'completed' && order.dossier_filename ? (
                              <Tooltip title="Download Dossier" arrow>
                                <Button
                                  variant="text"
                                  sx={DashStyles.actionButton}
                                  onClick={() => {
                                    if (order.label_claim_id) {
                                      handleDownloadFile(order.label_claim_id);
                                    } else {
                                      console.error('Missing label_claim_id for order:', order.id || 'unknown');
                                      alert('Cannot download: Invalid claim ID.');
                                    }
                                  }}
                                  disabled={!order.label_claim_id}
                                >
                                  Download
                                </Button>
                              </Tooltip>
                            ) : order.status === 'booking_paid' ? (
                              <Button
                                variant="text"
                                sx={DashStyles.actionButton}
                                onClick={() => {
                                  if (order.id && order.remaining_amount && order.label_claim_id) {
                                    handlePayRemaining(order.id, order.remaining_amount, order.label_claim_id);
                                  } else {
                                    console.error('Missing order details for payment:', order);
                                    alert('Cannot process payment: Invalid order details.');
                                  }
                                }}
                                disabled={paymentLoading || !order.remaining_amount}
                              >
                                {paymentLoading ? (
                                  <CircularProgress size={20} sx={{ marginRight: '8px' }} />
                                ) : null}
                                Pay Remaining (₹{order.remaining_amount || 'N/A'})
                              </Button>
                            ) : (
                              <Button
                                variant="text"
                                sx={DashStyles.actionButton}
                                onClick={() => {
                                  console.log('Retry Order clicked for order:', order.id || 'unknown');
                                  if (order.label_claim_id) {
                                    setSelectedClaim(order.label_claim_id);
                                    setCurrentView('labelClaims');
                                  } else {
                                    alert('Cannot retry: Invalid claim ID.');
                                  }
                                }}
                              >
                                Retry Order
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ padding: '20px', textAlign: 'center' }}>
                        <Typography>No order details available.</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
      <Box sx={DashStyles.footer}>
        <Typography sx={DashStyles.footerText}>© 2025 DealSphere. All rights reserved.</Typography>
        <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 } }}>
          {['Terms', 'Privacy', 'Contact'].map((text) => (
            <Typography key={text} sx={DashStyles.footerText}>{text}</Typography>
          ))}
        </Box>
      </Box>
      <Dialog open={showClaimDetails} onClose={() => setShowClaimDetails(false)} fullWidth maxWidth="sm">
        <DialogTitle>Claim Details</DialogTitle>
        <DialogContent>
          {selectedClaim && (
            <Grid container spacing={2}>
              {[
                { label: 'Grade', value: 'grade' },
                { label: 'Label', value: 'label_claim' },
                { label: 'Condition', value: 'condition' },
                { label: 'Ingredients', value: 'ingredients' },
                { label: 'Health Claims', value: 'health_claims' },
                {
                  label: 'Prototype',
                  value: 'prototype',
                  format: (v) => (v === 1 ? 'Yes' : 'No'),
                  color: (v) => (v === 1 ? 'success.main' : 'error.main'),
                },
                {
                  label: 'Clinical Trials',
                  value: 'clinical_trials',
                  format: (v) => (v === 1 ? 'Yes' : 'No'),
                  color: (v) => (v === 1 ? 'success.main' : 'error.main'),
                },
                {
                  label: 'Regulatory',
                  value: 'regulatory',
                  format: (v) => (v === 1 ? 'Yes' : 'No'),
                  color: (v) => (v === 1 ? 'success.main' : 'error.main'),
                },
                { label: 'Dosage', value: 'dosage' },
                { label: 'Format', value: 'format' },
                { label: 'Description', value: 'description' },
                { label: 'Amount', value: 'amount', format: (v) => `₹${v}` },
                { label: 'Booking Amount', value: 'booking_fee', format: (v) => `₹${v}` },
              ].map(({ label, value, format, color }) => {
                const claim = labelClaims.find((c) => c.id === selectedClaim) || {};
                const displayValue = format ? format(claim[value]) : claim[value] || 'N/A';
                return (
                  <Grid item xs={12} sm={6} key={value}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {label}:
                    </Typography>
                    <Typography variant="body2" color={color ? color(claim[value]) : 'inherit'}>
                      {displayValue}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClaimDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dashboard;